// ═══════════════════════════════════════════════════════════════════════════
// SEARCH-UI — fragments HTML purs de la recherche d'action
// Extrait de search-ui.ts (découpe `mountSearchUI`, 2026-09-27) : coquille de
// l'interface (champ, barre de chips, quick-filters, zone de résultats) et barre
// de chips (actifs + suggestions). Aucun état de closure, aucun accès au DOM —
// le câblage reste dans `mountSearchUI`. Comportement identique à l'inline.
// ═══════════════════════════════════════════════════════════════════════════
//
// Bare refs utilisées (résolues via core-globals.d.ts) : esc, t.

import type { Chip, SearchSuggestion } from './search-ui.js';

/** Coquille montée dans `root`. Sélecteurs par classes (pas d'id) pour
 * permettre plusieurs instances simultanées (page + overlay) sans collision. */
export function searchShellHtml(): string {
  return `
    <div class="search-ui-head" style="position:sticky;top:0;background:var(--bg2,#fff);padding:2px 0 10px;z-index:2;">
      <div style="display:flex;align-items:center;gap:9px;background:var(--bg2,#fff);border:1px solid var(--border,#d5dbe3);border-radius:12px;padding:10px 12px;">
        <svg class="icon" style="width:20px;height:20px;flex:none;opacity:.55" aria-hidden="true"><use href="/static/svg/icons.svg#icon-search"></use></svg>
        <input class="search-ui-input" type="search" autocomplete="off" spellcheck="false"
          placeholder="${esc(t('pageSearch.placeholder'))}" aria-label="${esc(t('pageSearch.placeholder'))}"
          style="flex:1;min-width:0;border:none;outline:none;background:transparent;font-size:16px;color:var(--text,#1b2733);" />
      </div>
      <div class="search-ui-chips" style="display:flex;flex-wrap:wrap;gap:6px;margin-top:8px;"></div>
      <div class="search-ui-quick" style="display:flex;flex-wrap:wrap;gap:6px;margin-top:8px;">
        <button type="button" class="search-quick" data-quick="mine" style="font-size:13px;border:1px solid var(--border,#d5dbe3);background:var(--bg2,#fff);border-radius:999px;padding:6px 13px;cursor:pointer;">${esc(t('pageSearch.quickMine'))}</button>
        <button type="button" class="search-quick" data-quick="late" style="font-size:13px;border:1px solid var(--border,#d5dbe3);background:var(--bg2,#fff);border-radius:999px;padding:6px 13px;cursor:pointer;">${esc(t('pageSearch.quickLate'))}</button>
      </div>
    </div>
    <div class="search-ui-results" style="margin-top:6px;"></div>`;
}

/** Barre de chips : les filtres actifs (retirables, `data-idx`), puis — s'il en
 * reste — le libellé « affiner » et jusqu'à six suggestions serveur non déjà
 * posées en chip (`data-type` / `data-id` / `data-label`). */
export function chipsBarHtml(
  chips: readonly Chip[],
  suggestions: readonly SearchSuggestion[],
): string {
  const active = chips
    .map(
      (c, i) =>
        `<button type="button" class="search-chip-active" data-idx="${i}" style="font-size:13px;color:var(--accent,#0f6e56);background:var(--accent-bg,#e8f6f0);border:1px solid var(--accent,#1d9e75);border-radius:999px;padding:6px 11px;cursor:pointer;">${esc(c.label)} ✕</button>`,
    )
    .join('');
  const chipIds = new Set(
    chips.filter((c) => 'id' in c).map((c) => c.kind + ':' + (c as { id: number }).id),
  );
  const suggBtns = suggestions
    .filter((s) => !chipIds.has(s.type + ':' + s.id))
    .slice(0, 6)
    .map(
      (s) =>
        `<button type="button" class="search-suggest" data-type="${esc(s.type)}" data-id="${s.id}" data-label="${esc(s.label)}" style="font-size:13px;color:var(--text2,#3a4653);background:var(--bg3,#f1f4f8);border:1px solid var(--border,#d5dbe3);border-radius:999px;padding:6px 11px;cursor:pointer;">+ ${esc(s.label)}</button>`,
    )
    .join('');
  const refine = suggBtns
    ? `<span style="font-size:12px;color:var(--text3,#646e7e);align-self:center;">${esc(t('pageSearch.refine'))}</span>`
    : '';
  return active + refine + suggBtns;
}
